import apiService from './api';
import { openDocument } from '../utils/openDocument';
import type { ApiResponse } from '../types/api';
import type {
  AreaMaintenance, CropRemoval, LeafRanges, LeafStandardsResearch, RemovalRates, RemovalResearch,
  SampleType, SoilSample, SoilSampleDetail, SoilSampleFormData, SoilReportUpload,
} from '../types/soil';

const BASE = '/orchard/soil';

class SoilService {
  async list(filters: { farm_id?: number; block_id?: number; cultivar_area_id?: number; sample_type?: SampleType } = {}): Promise<ApiResponse<SoilSample[]>> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v) qs.set(k, String(v));
    const q = qs.toString();
    return apiService.get(`${BASE}/samples${q ? `?${q}` : ''}`);
  }

  async get(id: number): Promise<ApiResponse<SoilSampleDetail>> {
    return apiService.get(`${BASE}/samples/${id}`);
  }

  async create(data: SoilSampleFormData): Promise<ApiResponse<SoilSampleDetail>> {
    return apiService.post(`${BASE}/samples`, data);
  }

  async update(id: number, data: SoilSampleFormData): Promise<ApiResponse<SoilSampleDetail>> {
    return apiService.put(`${BASE}/samples/${id}`, data);
  }

  async remove(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`${BASE}/samples/${id}`);
  }

  /**
   * Upload a lab report and have it read. A CSV is read by rule; a PDF or a
   * photo by AI, which can take a minute or two. Nothing is saved but the file.
   */
  async uploadReport(file: File, sampleType: SampleType = 'soil'): Promise<ApiResponse<SoilReportUpload>> {
    const form = new FormData();
    // Before the file: multer reads fields in order, and the route needs the type when the file arrives.
    form.append('sample_type', sampleType);
    form.append('file', file);
    return apiService.post(`${BASE}/reports`, form, undefined, { timeout: 240000 });
  }

  /** The route needs the bearer token, so the file cannot be a plain link. */
  async openReport(reportId: number, fileName: string | null): Promise<void> {
    await openDocument(`${BASE}/reports/${reportId}/file`, fileName || `soil-report-${reportId}`);
  }

  async getRemoval(): Promise<ApiResponse<CropRemoval[]>> {
    return apiService.get(`${BASE}/removal`);
  }

  async setRemoval(cropTypeId: number, rates: RemovalRates, source: string | null): Promise<ApiResponse<CropRemoval>> {
    return apiService.put(`${BASE}/removal/${cropTypeId}`, { rates, source });
  }

  /** Published removal figures for a crop, researched by AI with web search. A proposal. */
  async researchRemoval(cropTypeId: number): Promise<ApiResponse<RemovalResearch>> {
    return apiService.post(`${BASE}/removal/${cropTypeId}/research`, {}, undefined, { timeout: 270000 });
  }

  async setLeafStandards(cropTypeId: number, ranges: LeafRanges, source: string | null): Promise<ApiResponse<CropRemoval>> {
    return apiService.put(`${BASE}/leaf-standards/${cropTypeId}`, { ranges, source });
  }

  /** Published adequate leaf ranges for a crop, researched by AI with web search. A proposal. */
  async researchLeafStandards(cropTypeId: number): Promise<ApiResponse<LeafStandardsResearch>> {
    return apiService.post(`${BASE}/leaf-standards/${cropTypeId}/research`, {}, undefined, { timeout: 270000 });
  }

  async getMaintenance(areaId: number): Promise<ApiResponse<AreaMaintenance>> {
    return apiService.get(`${BASE}/cultivar-areas/${areaId}/maintenance`);
  }
}

export default new SoilService();
